import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerHeader,
  DialogDrawerTitle,
} from '@/components/ui/mobile-responsive-dialog-drawer';
import { getHelpCenterUrl } from '@/helpers/getHelpCenterUrl';
import {
  invalidateKindeProperties,
  useQueryKindeProperties,
} from '@/hooks/query/useQueryKindeProperties';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { isHeidiSupportReachable } from '@/utils/heidiReach';
import { Button } from '@heidi/design-system/button';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { Link } from '@heidi/design-system/link';
import { TypographyP2 } from '@heidi/design-system/typography';
import { ModalId } from '@heidi/schema/ml-scribe';
import { ExternalLink, Loader2, WifiOff } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';

/** The only surface that links to the firewall guide. */
const LOGIN_FIREWALL_HELP_ARTICLE_PATH = 'articles/8987799-login-in-issue';

const NetworkErrorModal = () => {
  const intl = useIntl();
  const { isOpen } = useQueuedModal(ModalId.T1_NETWORK_FIREWALL_ERROR_MODAL);
  const { isFetching } = useQueryKindeProperties();

  return (
    <DialogDrawer open={isOpen} dismissible={false} onOpenChange={() => {}}>
      <DialogDrawerContent
        hideCloseIcon
        className="flex flex-col items-center space-y-3 p-6 sm:h-fit sm:max-w-md"
      >
        <DialogDrawerHeader className="flex w-full flex-col items-start gap-y-2 text-left sm:gap-y-0.5">
          <FramedIcon state="error" size="lg" icon={<WifiOff />} className="mb-3" />
          <DialogDrawerTitle className="text-validation-destructive">
            There was an issue connecting to Heidi
          </DialogDrawerTitle>
          <TypographyP2 className="text-text-secondary">
            {isHeidiSupportReachable ? (
              <>
                Please check your internet connection, or contact support if this keeps
                occurring.
              </>
            ) : (
              <FormattedMessage
                defaultMessage="Please check your internet connection, or contact your IT team if this keeps occurring."
                id="WIAjl3u7Wr"
              />
            )}
          </TypographyP2>
          <Link
            href={getHelpCenterUrl(LOGIN_FIREWALL_HELP_ARTICLE_PATH, intl.locale)}
            target="_blank"
            rel="noopener noreferrer"
            variant="inline"
            size="sm"
          >
            Troubleshooting guide
            <ExternalLink />
          </Link>
        </DialogDrawerHeader>
        <Button
          size="lg"
          disableAutomaticSvgSizing
          className="w-full"
          disabled={isFetching}
          onClick={() => {
            void invalidateKindeProperties();
          }}
        >
          {isFetching ? (
            <>
              <Loader2 className="mr-1.5 size-4 animate-spin" />
              <FormattedMessage
                id="9MngB/VlG8"
                defaultMessage="Retrying..."
                description="NetworkErrorModal: in-flight label on the Retry button while the kinde-properties query refetches"
              />
            </>
          ) : (
            <FormattedMessage
              id="MKVi9OPZDd"
              defaultMessage="Retry"
              description="NetworkErrorModal: button label that refetches the kinde-properties query"
            />
          )}
        </Button>
      </DialogDrawerContent>
    </DialogDrawer>
  );
};

export default NetworkErrorModal;
